import { useCallback, useEffect } from "react"
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { useLocation } from "react-router-dom"
import { extractUrlParams } from "./url"

export const errorMessages = {
    400: 'Your purchase could not be completed. A bad request was made.',
    401: 'Your purchase could not be completed. Your card is unauthorized.',
    402: 'Your purchase could not be completed. Your card was declined.',
    403: 'Your purchase could not be completed. You must be the game creator, a game administrator, or a system super administrator.',
    404: 'Your purchase could not be completed. The game associated with the purchase was not found.',
    406: 'Your purchase could not be completed. Authentication with our payment provider Stripe was found to be invalid.',
    500: 'Your purchase could not be completed. A server error has occurred.',
    501: 'Your purchase could not be completed. Our payment provider Stripe reported invalid parameters.',
    502: 'Your purchase could not be completed. Our payment provider Stripe rated limited the transaction.',
    503: 'Your purchase could not be completed. Our payment provider Stripe reported a generic error.',
    504: 'Your purchase could not be completed. The network connectivity to our payment provider Stripe was down.',
}

export const usePayment = () => {
    const doPayment = async ({ action, onSuccess, onError }) => {
        try {
            const response = await action()
            onSuccess(response)
        } catch (e) {
            if (errorMessages[e.response?.status]) {
              onError(errorMessages[e.response.status]);
            }
        }
        window.scrollTo(0, 0);
    }

    return {
        doPayment,
    }
}

export const useHandleSubmitPayment = ({ onBefore, onError, onSuccess, mutation, params = {}, payload: sourcePayload }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { search } = useLocation();
    const { doPayment } = usePayment();
    const { isPaying, pay, error, data } = mutation;
    const payload = sourcePayload || extractUrlParams(search);
    const hasResolved = error || data

    const handleSubmit = useCallback(async (event, isResolving) => {
      event?.preventDefault();

      onBefore && onBefore();

      if (!stripe || !elements) {
        return;
      }

      let paymentPayload = {...payload}

      if (!isResolving) {
        const cardElement = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });
        if (error) {
          onError && onError(error);
          return;
        }
        paymentPayload = {
          stripe_id: paymentMethod?.id,
        }
      }
      
      doPayment({
        action: () => pay({
          ...params,
          ...paymentPayload,
        }),
        onSuccess: (response) => {
          const { secret, status } = response?.data || {}
          if (status === 'requires_action' && secret) {
            stripe.handleNextAction({
              clientSecret: secret,
            })
          } else {
            onSuccess && onSuccess()
          }
        },
        onError,
      })
    }, [
        params, doPayment, elements, pay, 
        payload, stripe,
        onBefore, onError, onSuccess, 
    ])
    
    useEffect(() => {
      if (!isPaying && !hasResolved && Object.keys(payload)?.length > 0) {
        handleSubmit(null, true)
      }
    }, [payload, handleSubmit, hasResolved, isPaying])

    return handleSubmit
}

export default usePayment